import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { CellResolution, EntryMutation, EntryMutationInput, MutationAcknowledgement, MutationAudit, OwnerDocument } from './types'
import { entryCellKey } from './types'

type MetadataRecord = { key: string; value: string | number | boolean }

interface OpsMatrixDatabase extends DBSchema {
  ownerDocuments: { key: string; value: OwnerDocument }
  entryMutations: {
    key: string
    value: EntryMutation
    indexes: { byState: EntryMutation['state']; byCell: string; bySequence: number }
  }
  mutationAudit: { key: string; value: MutationAudit; indexes: { byCell: string } }
  cellResolutions: { key: string; value: CellResolution }
  metadata: { key: string; value: MetadataRecord }
}

export type EnqueueResult = { mutation: EntryMutation; duplicate: boolean }

export interface OfflineStore {
  getOwnerDocument<T = unknown>(ownerId: string): Promise<OwnerDocument<T> | undefined>
  putOwnerDocument<T = unknown>(document: OwnerDocument<T>): Promise<void>
  enqueue(input: EntryMutationInput): Promise<EnqueueResult>
  listPending(): Promise<EntryMutation[]>
  markSending(ids: string[], attemptedAt: string): Promise<void>
  markQueued(ids: string[], errorCode?: string): Promise<void>
  acknowledge(acks: MutationAcknowledgement[]): Promise<void>
  pendingCount(): Promise<number>
  getCellResolution(cellKey: string): Promise<CellResolution | undefined>
  listAudit(cellKey: string): Promise<MutationAudit[]>
  getMetadata(key: string): Promise<string | number | boolean | undefined>
  putMetadata(key: string, value: string | number | boolean): Promise<void>
  deleteMetadata(key: string): Promise<void>
  prepareUpgradeCheckpoint(): Promise<number>
  close(): void
}

const DATABASE_NAME = 'recordbreaker-matrix-codex'
const DATABASE_VERSION = 1

function newId() {
  return globalThis.crypto?.randomUUID?.() ?? `mutation-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export async function createOfflineStore(databaseName = DATABASE_NAME): Promise<OfflineStore> {
  const db = await openDB<OpsMatrixDatabase>(databaseName, DATABASE_VERSION, {
    upgrade(database) {
      database.createObjectStore('ownerDocuments', { keyPath: 'ownerId' })
      const mutations = database.createObjectStore('entryMutations', { keyPath: 'id' })
      mutations.createIndex('byState', 'state')
      mutations.createIndex('byCell', 'cellKey')
      mutations.createIndex('bySequence', 'clientSequence')
      const audit = database.createObjectStore('mutationAudit', { keyPath: 'id' })
      audit.createIndex('byCell', 'cellKey')
      database.createObjectStore('cellResolutions', { keyPath: 'cellKey' })
      database.createObjectStore('metadata', { keyPath: 'key' })
    },
  })
  return new IndexedDbOfflineStore(db)
}

class IndexedDbOfflineStore implements OfflineStore {
  constructor(private readonly db: IDBPDatabase<OpsMatrixDatabase>) {}

  async getOwnerDocument<T>(ownerId: string) {
    return await this.db.get('ownerDocuments', ownerId) as OwnerDocument<T> | undefined
  }

  async putOwnerDocument<T>(document: OwnerDocument<T>) {
    const current = await this.db.get('ownerDocuments', document.ownerId)
    if (current && current.version > document.version) return
    await this.db.put('ownerDocuments', document as OwnerDocument)
  }

  async enqueue(input: EntryMutationInput): Promise<EnqueueResult> {
    const tx = this.db.transaction(['entryMutations', 'metadata'], 'readwrite')
    const mutationId = input.id ?? newId()
    const existing = await tx.objectStore('entryMutations').get(mutationId)
    const normalized = { outcome: input.outcome ?? null, deleted: input.deleted ?? input.outcome == null }
    if (!input.ownerId || !input.operationId || !/^\d{4}-\d{2}-\d{2}$/.test(input.date)) throw new Error('Entry mutations require an owner, operation, and ISO date.')
    if (normalized.deleted !== (normalized.outcome === null)) throw new Error('Deleted entries must have a null outcome; recorded entries must have an outcome.')
    if (existing) {
      const same = existing.ownerId === input.ownerId && existing.operationId === input.operationId && existing.date === input.date && existing.outcome === normalized.outcome && existing.deleted === normalized.deleted
      if (!same) throw new Error('An idempotency key cannot be reused for different entry data.')
      await tx.done
      return { mutation: existing, duplicate: true }
    }
    const sequenceRecord = await tx.objectStore('metadata').get('clientSequence')
    const sequence = Number(sequenceRecord?.value ?? 0) + 1
    const deviceRecord = await tx.objectStore('metadata').get('deviceId')
    const deviceId = typeof deviceRecord?.value === 'string' ? deviceRecord.value : newId()
    const now = new Date().toISOString()
    const mutation: EntryMutation = {
      id: mutationId,
      ownerId: input.ownerId,
      operationId: input.operationId,
      date: input.date,
      outcome: normalized.outcome,
      deleted: normalized.deleted,
      cellKey: entryCellKey(input.ownerId, input.operationId, input.date),
      clientCreatedAt: now,
      clientSequence: sequence,
      deviceId,
      attempts: 0,
      state: 'queued',
    }
    await tx.objectStore('metadata').put({ key: 'clientSequence', value: sequence })
    await tx.objectStore('metadata').put({ key: 'deviceId', value: deviceId })
    await tx.objectStore('entryMutations').put(mutation)
    await tx.done
    return { mutation, duplicate: false }
  }

  async listPending() {
    const all = await this.db.getAllFromIndex('entryMutations', 'bySequence')
    return all.filter((mutation) => mutation.state !== 'failed').sort((a, b) => a.clientSequence - b.clientSequence)
  }

  async markSending(ids: string[], attemptedAt: string) {
    const tx = this.db.transaction('entryMutations', 'readwrite')
    for (const id of ids) {
      const mutation = await tx.store.get(id)
      if (mutation) await tx.store.put({ ...mutation, state: 'sending', attempts: mutation.attempts + 1, lastAttemptAt: attemptedAt, lastErrorCode: undefined })
    }
    await tx.done
  }

  async markQueued(ids: string[], errorCode?: string) {
    const tx = this.db.transaction('entryMutations', 'readwrite')
    for (const id of ids) {
      const mutation = await tx.store.get(id)
      if (mutation) await tx.store.put({ ...mutation, state: 'queued', lastErrorCode: errorCode })
    }
    await tx.done
  }

  async acknowledge(acks: MutationAcknowledgement[]) {
    const tx = this.db.transaction(['entryMutations', 'mutationAudit', 'cellResolutions'], 'readwrite')
    for (const ack of acks) {
      const queued = await tx.objectStore('entryMutations').get(ack.id)
      if (!queued) continue
      const current = await tx.objectStore('cellResolutions').get(ack.cellKey)
      const resolution = ack.resolution
      const becameWinner = resolution.winnerMutationId === ack.id
      const previousWinnerMutationId = resolution.previousWinnerMutationId ?? current?.winnerMutationId
      await tx.objectStore('cellResolutions').put(resolution)
      await tx.objectStore('mutationAudit').put({
        id: ack.auditId ?? ack.id,
        mutationId: ack.id,
        cellKey: ack.cellKey,
        clientCreatedAt: queued.clientCreatedAt,
        serverReceivedAt: ack.serverReceivedAt,
        outcome: ack.outcome,
        deleted: ack.deleted,
        previousWinnerMutationId,
        becameWinner,
      })
      await tx.objectStore('entryMutations').delete(ack.id)
    }
    await tx.done
  }

  async pendingCount() {
    return (await this.db.getAll('entryMutations')).length
  }

  async getCellResolution(cellKey: string) {
    return await this.db.get('cellResolutions', cellKey)
  }

  async listAudit(cellKey: string) {
    return await this.db.getAllFromIndex('mutationAudit', 'byCell', cellKey)
  }

  async getMetadata(key: string) {
    return (await this.db.get('metadata', key))?.value
  }

  async putMetadata(key: string, value: string | number | boolean) {
    await this.db.put('metadata', { key, value })
  }

  async deleteMetadata(key: string) {
    await this.db.delete('metadata', key)
  }

  async prepareUpgradeCheckpoint() {
    const pending = await this.listPending()
    const fingerprint = pending.map((item) => item.id).sort().join(',')
    await this.db.put('metadata', { key: 'upgradeCheckpoint', value: `${new Date().toISOString()}|${fingerprint}` })
    return pending.length
  }

  close() { this.db.close() }
}
